/** Inventory list filter keys — aligned with dashboard insight categories */
export type InventoryFilterKey =
  | 'belowShelfLevel'
  | 'deadItems'
  | 'nearExpiry'
  | 'outOfStock'
  | 'inactive'
  | 'activeOnly';

export const DEFAULT_NEAR_EXPIRY_DAYS = 30;
export const MIN_NEAR_EXPIRY_DAYS = 1;
export const MAX_NEAR_EXPIRY_DAYS = 365;

export interface InventoryFiltersState {
  belowShelfLevel?: boolean;
  deadItems?: boolean;
  nearExpiry?: boolean;
  nearExpiryDays?: number;
  outOfStock?: boolean;
  inactive?: boolean;
  activeOnly?: boolean;
}

export interface InventoryFilterOption {
  key: InventoryFilterKey;
  label: string;
  description: string;
  icon: string;
  group: 'stock' | 'status';
}

export const INVENTORY_FILTER_OPTIONS: InventoryFilterOption[] = [
  {
    key: 'belowShelfLevel',
    label: 'Below shelf level',
    description: 'Quantity is at or below the shelf level threshold',
    icon: 'bx-down-arrow-alt',
    group: 'stock',
  },
  {
    key: 'deadItems',
    label: 'Dead stock',
    description: 'Active items that have never been sold',
    icon: 'bx-trash',
    group: 'stock',
  },
  {
    key: 'nearExpiry',
    label: 'Near expiry',
    description: 'Items expiring within a number of days and still in stock',
    icon: 'bx-time-five',
    group: 'stock',
  },
  {
    key: 'outOfStock',
    label: 'Out of stock',
    description: 'Quantity is zero',
    icon: 'bx-package',
    group: 'stock',
  },
  {
    key: 'activeOnly',
    label: 'Active only',
    description: 'Show only active inventory rows',
    icon: 'bx-check-circle',
    group: 'status',
  },
  {
    key: 'inactive',
    label: 'Inactive only',
    description: 'Show only deactivated inventory rows',
    icon: 'bx-block',
    group: 'status',
  },
];

/** Maps dashboard insight category → default list filters */
export const DASHBOARD_INSIGHT_TO_FILTER: Record<string, InventoryFiltersState> = {
  dead: { deadItems: true, activeOnly: true },
  belowShelf: { belowShelfLevel: true, activeOnly: true },
  nearExpiry: { nearExpiry: true, activeOnly: true, nearExpiryDays: DEFAULT_NEAR_EXPIRY_DAYS },
};

const URL_FILTER_PARAM = 'filter';
const URL_NEAR_EXPIRY_DAYS_PARAM = 'nearExpiryDays';

const FILTER_KEYS: InventoryFilterKey[] = [
  'belowShelfLevel',
  'deadItems',
  'nearExpiry',
  'outOfStock',
  'inactive',
  'activeOnly',
];

export function countActiveFilters(filters: InventoryFiltersState): number {
  return FILTER_KEYS.filter((key) => filters[key]).length;
}

export function parseFiltersFromSearchParams(searchParams: URLSearchParams): InventoryFiltersState {
  const raw = searchParams.get(URL_FILTER_PARAM);
  if (!raw) return {};

  const result: InventoryFiltersState = {};
  raw.split(',').forEach((part) => {
    const key = part.trim() as InventoryFilterKey;
    if (FILTER_KEYS.includes(key)) {
      result[key] = true;
    }
  });

  const daysRaw = searchParams.get(URL_NEAR_EXPIRY_DAYS_PARAM);
  if (daysRaw) {
    const days = parseInt(daysRaw, 10);
    if (!Number.isNaN(days) && days >= MIN_NEAR_EXPIRY_DAYS && days <= MAX_NEAR_EXPIRY_DAYS) {
      result.nearExpiryDays = days;
    }
  }

  if (result.nearExpiry && result.nearExpiryDays == null) {
    result.nearExpiryDays = DEFAULT_NEAR_EXPIRY_DAYS;
  }

  return result;
}

export function filtersToSearchParams(filters: InventoryFiltersState): URLSearchParams {
  const params = new URLSearchParams();
  const active = FILTER_KEYS.filter((key) => filters[key]);
  if (active.length > 0) {
    params.set(URL_FILTER_PARAM, active.join(','));
  }
  if (filters.nearExpiry && filters.nearExpiryDays != null) {
    params.set(URL_NEAR_EXPIRY_DAYS_PARAM, String(filters.nearExpiryDays));
  }
  return params;
}

/** Query params sent to the inventories paginated API */
export function filtersToApiParams(filters: InventoryFiltersState): Record<string, boolean | number> {
  const params: Record<string, boolean | number> = {};

  if (filters.belowShelfLevel) params.belowShelfLevelOnly = true;
  if (filters.deadItems) params.deadItemsOnly = true;
  if (filters.nearExpiry) {
    params.nearExpiryOnly = true;
    params.nearExpiryDays = filters.nearExpiryDays ?? DEFAULT_NEAR_EXPIRY_DAYS;
  }
  if (filters.outOfStock) params.outOfStockOnly = true;
  if (filters.inactive) params.isActive = false;
  else if (filters.activeOnly) params.isActive = true;

  return params;
}

export function getFilterLabel(key: InventoryFilterKey, filters?: InventoryFiltersState): string {
  if (key === 'nearExpiry' && filters?.nearExpiryDays != null) {
    return `Near expiry (${filters.nearExpiryDays} days)`;
  }
  return INVENTORY_FILTER_OPTIONS.find((o) => o.key === key)?.label ?? key;
}

export function cloneFilters(filters: InventoryFiltersState): InventoryFiltersState {
  return { ...filters };
}

export function removeFilter(
  filters: InventoryFiltersState,
  key: InventoryFilterKey,
): InventoryFiltersState {
  const next = cloneFilters(filters);
  delete next[key];
  if (key === 'nearExpiry') {
    delete next.nearExpiryDays;
  }
  return next;
}
